/**
 * Design-tab concept index (design §16) — one searchable list across both storage worlds:
 * section items (markdown, via parseItems + the Tags: line), screens and levels (JSON).
 * Powers the ⌘K palette, clickable tag chips, and (later) `level://`/`content://` links —
 * one navigate-to-concept resolver for all of them.
 */
import type { DesignProject, LevelEntry, Screen } from '@volley/crucible-ipc-types';
import { parseItems, itemModeFor } from './section-items';
import { extractTags } from './tags';

export type ConceptRef =
  | {
      type: 'item';
      id: string;
      name: string;
      slug: string;
      tags: string[];
      sectionFilename: string;
      sectionTitle: string;
      parentName?: string;
    }
  | { type: 'screen'; id: string; name: string; tags: string[]; surface: string }
  | { type: 'level'; id: string; name: string; tags: string[]; kind: string; summary?: string };

export interface DesignIndex {
  concepts: ConceptRef[];
}

/** Build the index. `readSectionText` is injected (anvil-api in the app, a stub in tests). */
export async function buildDesignIndex(args: {
  project: DesignProject;
  screens: Screen[];
  levels: LevelEntry[];
  readSectionText: (filename: string) => Promise<string>;
}): Promise<DesignIndex> {
  const { project, screens, levels, readSectionText } = args;
  const concepts: ConceptRef[] = [];

  for (const s of project.sections) {
    if (!itemModeFor(s)) continue;
    let md = '';
    try {
      md = await readSectionText(s.filename);
    } catch {
      continue;
    }
    for (const item of parseItems(md).items) {
      concepts.push({
        type: 'item',
        id: item.id,
        name: item.name,
        slug: item.slug,
        tags: extractTags(item.body).tags,
        sectionFilename: s.filename,
        sectionTitle: s.title,
      });
      for (const child of item.children) {
        concepts.push({
          type: 'item',
          id: child.id,
          name: child.name,
          slug: child.slug,
          tags: extractTags(child.body).tags,
          sectionFilename: s.filename,
          sectionTitle: s.title,
          parentName: item.name,
        });
      }
    }
  }
  for (const sc of screens) {
    concepts.push({
      type: 'screen',
      id: sc.id,
      name: sc.name,
      tags: sc.tags ?? [],
      surface: sc.surface,
    });
  }
  for (const l of levels) {
    concepts.push({
      type: 'level',
      id: l.id,
      name: l.name,
      tags: l.tags ?? [],
      kind: l.kind,
      summary: l.summary,
    });
  }
  return { concepts };
}

/** Normalize a concept name for `@Mention` matching: lowercase, strip non-alphanumerics —
 *  so `@FirePit`, `@fire-pit` and `@firepit` all resolve to "Fire Pit". */
export function normalizeConceptName(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '');
}

/** Resolve an inline `@Mention` to the concepts whose NAME matches (normalized).
 *  One hit → navigate straight there; several/none → the caller falls back to search. */
export function resolveMention(index: DesignIndex, name: string): ConceptRef[] {
  const needle = normalizeConceptName(name);
  if (!needle) return [];
  return index.concepts.filter((c) => normalizeConceptName(c.name) === needle);
}

/** All tags with usage counts, most-used first. */
export function tagCounts(index: DesignIndex): { tag: string; count: number }[] {
  const counts = new Map<string, number>();
  for (const c of index.concepts) {
    for (const t of c.tags) counts.set(t, (counts.get(t) ?? 0) + 1);
  }
  return [...counts.entries()]
    .map(([tag, count]) => ({ tag, count }))
    .sort((a, b) => b.count - a.count || a.tag.localeCompare(b.tag));
}

/**
 * Match concepts against a query. `#foo` / `tag:foo` restricts to tags; otherwise plain
 * case-insensitive matching over name > slug/summary > tags, ranked.
 */
export function matchConcepts(index: DesignIndex, query: string, limit = 30): ConceptRef[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  const tagOnly = q.startsWith('#') || q.startsWith('tag:');
  const needle = tagOnly ? q.replace(/^#|^tag:/, '').trim() : q;
  if (!needle) return [];

  const normNeedle = normalizeConceptName(needle);
  const scored: { c: ConceptRef; score: number }[] = [];
  for (const c of index.concepts) {
    const name = c.name.toLowerCase();
    const slug = (
      c.type === 'item' ? c.slug : c.type === 'level' ? (c.summary ?? '') : ''
    ).toLowerCase();
    const tagHit = c.tags.some((t) => t.toLowerCase().includes(needle));
    if (tagOnly) {
      if (tagHit) scored.push({ c, score: c.tags.some((t) => t.toLowerCase() === needle) ? 2 : 1 });
      continue;
    }
    let score = -1;
    if (name.startsWith(needle)) score = 3;
    else if (name.includes(needle)) score = 2;
    // `@FirePit`-style queries: match names with spacing/punctuation stripped.
    else if (normNeedle && normalizeConceptName(name).includes(normNeedle)) score = 2;
    else if (slug.includes(needle)) score = 1;
    else if (tagHit) score = 1;
    if (score >= 0) scored.push({ c, score });
  }
  return scored
    .sort((a, b) => b.score - a.score || a.c.name.localeCompare(b.c.name))
    .slice(0, limit)
    .map((x) => x.c);
}
